import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, AlertCircle, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { useProduct } from "@/hooks/useProduct";
import { fetchTopExpressionsFromDashboard } from "@/store/slices/meetingSlice";
import { ExpressionAssessment } from "@/components/ExpressionAssessment";
import { logger } from "@/utils/logger";

const PracticeExpressions = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const config = useProduct();

  const [currentIndex, setCurrentIndex] = useState(0);

  // Detect if coming from dashboard via query param
  const searchParams = new URLSearchParams(location.search);
  const fromDashboard = searchParams.get("from") === "dashboard";

  const preparationState = useAppSelector(
    (state) => state.meeting.preparationSubmission
  );

  // Load expressions from dashboard if needed
  useEffect(() => {
    const loadExpressionsFromDashboard = async () => {
      if (fromDashboard && meetingId) {
        logger.log(
          "[PracticeExpressions] Loading from dashboard for meeting:",
          meetingId
        );
        try {
          await dispatch(fetchTopExpressionsFromDashboard(meetingId)).unwrap();
        } catch (error: any) {
          logger.error(
            "[PracticeExpressions] Failed to load from dashboard:",
            error
          );
          toast({
            title: config.i18n.practiceExpressions.failedToLoad,
            description:
              error || config.i18n.practiceExpressions.tryAgainFromDashboard,
            variant: "destructive",
          });
          navigate("/dashboard");
        }
      }
    };

    loadExpressionsFromDashboard();
  }, [fromDashboard, meetingId, dispatch, toast, navigate]);

  // Show loading state from Redux
  const isLoading =
    preparationState.status === "submitting" ||
    preparationState.status === "fetching" ||
    preparationState.status === "loading-audio";

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">
            {config.i18n.practiceExpressions.loadingExpressions}
          </p>
        </div>
      </div>
    );
  }

  if (
    !preparationState.expressions ||
    preparationState.expressions.length === 0
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {config.i18n.practiceExpressions.noExpressionsTitle}
          </h2>
          <p className="text-muted-foreground mb-6">
            {config.i18n.practiceExpressions.noExpressionsDescription}
          </p>
          <Button onClick={() => navigate(`/meeting/${meetingId}/prepare`)}>
            {config.i18n.practiceExpressions.goToPreparation}
          </Button>
        </div>
      </div>
    );
  }

  const expressions = preparationState.expressions;
  const currentExpression = expressions[currentIndex];
  const isFirstExpression = currentIndex === 0;
  const isLastExpression = currentIndex === expressions.length - 1;

  const handlePrevious = () => {
    if (!isFirstExpression) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (!isLastExpression) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-foreground">
            {config.i18n.practiceExpressions.title}
          </h1>
          <p className="text-muted-foreground text-lg">
            {config.i18n.practiceExpressions.subtitle}
          </p>
        </div>

        {/* Current Expression with Assessment */}
        <ExpressionAssessment
          expression={currentExpression}
          modelAudioUrl={preparationState.audioBlobUrls[currentExpression.id]}
          expressionNumber={currentIndex + 1}
          totalExpressions={expressions.length}
          isLastExpression={isLastExpression}
          onNextExpression={isLastExpression ? undefined : handleNext}
          onComplete={() =>
            fromDashboard
              ? navigate("/dashboard")
              : navigate(`/meeting/${meetingId}/summary?from=practice`)
          }
        />

        {/* Navigation Links - Text-based like mockup */}
        <div className="flex justify-between items-center pt-8 border-t border-border">
          <button
            onClick={() => {
              navigate(
                fromDashboard ? "/dashboard" : `/meeting/${meetingId}/summary`
              );
            }}
            className="text-foreground hover:text-primary transition-colors text-base font-medium flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {fromDashboard
              ? config.i18n.practiceExpressions.backToDashboard
              : config.i18n.practiceExpressions.backToSummary}
          </button>

          {!fromDashboard && (
            <button
              onClick={() => navigate(`/meeting/${meetingId}/simulate`)}
              className="text-foreground hover:text-primary transition-colors text-base font-medium flex items-center gap-2"
            >
              {config.i18n.practiceExpressions.continueToSimulation}
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PracticeExpressions;
